import axios, { AxiosInstance, AxiosResponse } from 'axios'
import type { ApiResponse, AuthResponse, LoginRequest, RegisterRequest, User, Wallet, Transaction, MerchantLocation, MerchantCategory, QRPaymentCode, Contact, DiscoveredDjeloUser, FriendRequest } from '@/types'

class ApiClient {
  private client: AxiosInstance
  private refreshPromise: Promise<void> | null = null

  constructor() {
    // Use relative API in dev to keep cookies same-origin
    const configuredApiUrl = import.meta.env.VITE_API_URL || '/api'
    const apiUrl = import.meta.env.DEV ? '/api' : configuredApiUrl
    // Remove trailing slash if present
    const baseURL = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl
    
    this.client = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Enable cookies
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Response interceptor for handling errors
    this.client.interceptors.response.use(
      (response: any) => {
        return response
      },
      async (error: any) => {
        // Log errors for debugging
        if (error.response) {
          console.error('API Error:', {
            status: error.response.status,
            url: error.config?.url,
            data: error.response.data,
            message: error.response.data?.error || error.message
          })
        }
        
        // Handle 401 - try refresh once for cookie-based sessions
        if (error.response?.status === 401) {
          const originalRequest = error.config || {}
          const url = originalRequest.url || ''
          const isRefreshRequest = url.includes('/token/refresh')
          const isPublicPage = [
            '/login',
            '/register',
            '/reset-password',
            '/reset-password-request',
          ].some((path) => window.location.pathname === path)
          const isPublicAuthRequest =
            url.includes('/users/login') ||
            url.includes('/users/register') ||
            url.includes('/auth/request-password-reset') ||
            url.includes('/auth/confirm-password-reset')

          if (!isRefreshRequest && !isPublicAuthRequest && !isPublicPage && !originalRequest._retry) {
            originalRequest._retry = true

            try {
              await this.refreshSession()
              return this.client(originalRequest)
            } catch (refreshError) {
              if (window.location.pathname !== '/login') {
                window.location.href = '/login'
              }
              return Promise.reject(refreshError)
            }
          }

          if (!isPublicAuthRequest && !isPublicPage && window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
        }
        
        // Handle 403 - Forbidden (Account not active)
        if (error.response?.status === 403) {
          const errorMessage = error.response?.data?.error || error.response?.data?.message
          if (errorMessage?.toLowerCase().includes('account is not active') || 
              errorMessage?.toLowerCase().includes('conta não está ativa')) {
            // Account is not active - show user-friendly message
            console.warn('Account is not active. User needs to activate their account.')
            // Don't redirect, let components handle this error
          }
        }
        
        // Return error so components can handle it
        return Promise.reject(error)
      }
    )
  }

  private async refreshSession(): Promise<void> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.client
        .post('/token/refresh/', {})
        .then(() => undefined)
        .finally(() => {
          this.refreshPromise = null
        })
    }

    return this.refreshPromise
  }

  // Auth endpoints
  async login(credentials: LoginRequest): Promise<AxiosResponse<ApiResponse<AuthResponse>>> {
    return this.client.post('/users/login', credentials)
  }

  async register(data: RegisterRequest): Promise<AxiosResponse<ApiResponse<AuthResponse>>> {
    return this.client.post('/users/register', data)
  }

  async requestPasswordReset(email: string): Promise<AxiosResponse<ApiResponse<{ message: string }>>> {
    return this.client.post('/auth/request-password-reset/', { email: email.trim().toLowerCase() })
  }

  async confirmPasswordReset(data: { token: string; new_password: string; confirm_password: string }): Promise<AxiosResponse<ApiResponse<{ user: User }>>> {
    return this.client.post('/auth/confirm-password-reset/', data)
  }

  async refreshToken(token: string): Promise<AxiosResponse<ApiResponse<{ token: string }>>> {
    return this.client.post('/token/refresh', { refresh: token })
  }

  async logout(): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.client.post('/token/logout')
  }

  // User endpoints
  async getProfile(): Promise<AxiosResponse<ApiResponse<User>>> {
    return this.client.get('/users/me')
  }

  async updateProfile(data: Partial<User>): Promise<AxiosResponse<ApiResponse<User>>> {
    return this.client.patch('/users/me', data)
  }

  async searchUsers(query: string): Promise<AxiosResponse<ApiResponse<User[]>>> {
    return this.client.get('/users/search', { params: { q: query } })
  }

  // User Profile endpoints - Use /users/me since /profiles doesn't exist in API
  async getUserProfile(): Promise<AxiosResponse<ApiResponse<any>>> {
    // Use /users/me endpoint which returns user data including profile information
    return this.client.get('/users/me')
  }

  async updateUserProfile(data: {
    first_name?: string
    last_name?: string
    address?: string
    city?: string
    postal_code?: string
    date_of_birth?: string
    nationality?: string
  }): Promise<AxiosResponse<ApiResponse<any>>> {
    // Use /users/me PATCH endpoint to update profile
    return this.client.patch('/users/me', data)
  }

  // Wallet endpoints
  async getWallets(): Promise<AxiosResponse<ApiResponse<Wallet[]>>> {
    return this.client.get('/wallets')
  }

  async getWallet(id: string): Promise<AxiosResponse<ApiResponse<Wallet>>> {
    return this.client.get(`/wallets/${id}`)
  }

  async getWalletFeeSchedule(id: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.client.get(`/wallets/${id}/fee-schedule`)
  }

  async createWallet(data: { currency: string; daily_limit?: number; monthly_limit?: number }): Promise<AxiosResponse<ApiResponse<Wallet>>> {
    return this.client.post('/wallets', data)
  }

  async updateWalletLimits(id: string, data: { daily_limit?: number; monthly_limit?: number }): Promise<AxiosResponse<any>> {
    return this.client.patch(`/wallets/${id}/update_limits`, data)
  }

  async deleteWallet(id: string): Promise<AxiosResponse<any>> {
    return this.client.delete(`/wallets/${id}`)
  }

  async setDefaultWallet(id: string): Promise<AxiosResponse<any>> {
    return this.client.post(`/wallets/${id}/set_default`)
  }

  async transferMoney(fromWalletId: string, data: { to_wallet_id: string; amount: number; description?: string }): Promise<AxiosResponse<any>> {
    return this.client.post(`/wallets/${fromWalletId}/transfer`, data)
  }

  async generateReceiveQR(data: {
    wallet_id: string
    amount: number
    description?: string
  }): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.client.post('/wallets/generate_receive_qr/', data)
  }

  async lookupReceiveQR(qrHash: string): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.client.post('/wallets/lookup_receive_qr/', { qr_hash: qrHash })
  }

  async processReceiveQR(data: {
    qr_hash: string
    wallet_id: string
    amount: number
    currency?: string
    description?: string
  }): Promise<AxiosResponse<ApiResponse<Transaction>>> {
    return this.client.post('/wallets/process_receive_qr/', data)
  }

  // Transaction endpoints
  async getTransactions(params?: {
    page?: number
    limit?: number
    type?: string
    status?: string
    search?: string
  }): Promise<AxiosResponse<ApiResponse<Transaction[]>>> {
    return this.client.get('/transactions', { params })
  }

  async getTransaction(id: string): Promise<AxiosResponse<ApiResponse<Transaction>>> {
    return this.client.get(`/transactions/${id}`)
  }

  async createTransaction(data: {
    from_wallet_id?: string
    to_wallet_id?: string
    to_phone?: string
    to_email?: string
    amount: number
    currency: string
    description?: string
  }): Promise<AxiosResponse<ApiResponse<Transaction>>> {
    return this.client.post('/transactions/send_money', data)
  }

  // QR Code endpoints
  async scanQRCode(qrData: string): Promise<AxiosResponse<ApiResponse<QRPaymentCode>>> {
    // Check if qrData is a simple hash (64 hex characters) or JSON
    const isHash = /^[a-f0-9]{64}$/i.test(qrData.trim())
    
    if (isHash) {
      // If it's a simple hash, send as qr_code_hash
      return this.client.post('/qr-code/scan', { qr_code_hash: qrData.trim() })
    } else {
      // Otherwise, send as qr_data (JSON string)
      return this.client.post('/qr-code/scan', { qr_data: qrData })
    }
  }

  async processQRPayment(data: {
    qr_code_id: string
    amount: number
    description?: string
  }): Promise<AxiosResponse<ApiResponse<Transaction>>> {
    return this.client.post('/qr-code/process-payment', data)
  }

  // Merchant endpoints (map locations)
  async getMerchants(params?: {
    lat?: number
    lng?: number
    radius?: number
    category?: string
    search?: string
    min_rating?: number
    promotions?: boolean
  }): Promise<AxiosResponse<ApiResponse<MerchantLocation[]>>> {
    const mapped: Record<string, any> = {}
    if (params?.lat !== undefined) mapped.latitude = params.lat
    if (params?.lng !== undefined) mapped.longitude = params.lng
    if (params?.radius !== undefined) mapped.radius = params.radius
    if (params?.category !== undefined) mapped.category = params.category
    if (params?.search !== undefined) mapped.search = params.search
    if (params?.min_rating !== undefined) mapped.min_rating = params.min_rating
    if (params?.promotions !== undefined) mapped.promotions = params.promotions
    return this.client.get('/map/locations', { params: mapped })
  }

  async getMerchant(id: string): Promise<AxiosResponse<ApiResponse<MerchantLocation>>> {
    return this.client.get(`/merchants/${id}`)
  }

  async getMerchantCategories(): Promise<AxiosResponse<ApiResponse<MerchantCategory[]>>> {
    return this.client.get('/map/categories')
  }

  async submitBusinessApplication(formData: FormData): Promise<AxiosResponse<any>> {
    return this.client.post('/merchant-applications', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  async getBusinessApplications(): Promise<AxiosResponse<ApiResponse<any[]>>> {
    return this.client.get('/merchant-applications')
  }

  // Notification endpoints - Not available in current API, return empty responses
  async getNotifications(): Promise<AxiosResponse<ApiResponse<any[]>>> {
    // Notifications endpoint doesn't exist in API, return empty array
    return Promise.resolve({
      data: { success: true, data: [] },
      status: 200,
      statusText: 'OK',
      headers: {},
      config: {} as any
    } as AxiosResponse<ApiResponse<any[]>>)
  }
  
  async markNotificationAsRead(id: string): Promise<AxiosResponse<ApiResponse<any>>> {
    // Notifications endpoint doesn't exist in API
    return Promise.resolve({
      data: { success: true, data: {} },
      status: 200,
      statusText: 'OK',
      headers: {},
      config: {} as any
    } as AxiosResponse<ApiResponse<any>>)
  }
  
  async markAllNotificationsAsRead(): Promise<AxiosResponse<ApiResponse<any>>> {
    // Notifications endpoint doesn't exist in API
    return Promise.resolve({
      data: { success: true, data: {} },
      status: 200,
      statusText: 'OK',
      headers: {},
      config: {} as any
    } as AxiosResponse<ApiResponse<any>>)
  }
  
  async getUnreadCount(): Promise<AxiosResponse<ApiResponse<{ unread_count: number }>>> {
    // Notifications endpoint doesn't exist in API, return 0
    return Promise.resolve({
      data: { success: true, data: { unread_count: 0 } },
      status: 200,
      statusText: 'OK',
      headers: {},
      config: {} as any
    } as AxiosResponse<ApiResponse<{ unread_count: number }>>)
  }

  // KYC endpoints
  async uploadKycDocument(file: File, documentType: 'id_card' | 'passport' | 'proof_of_address'): Promise<AxiosResponse<ApiResponse<any>>> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('document_type', documentType)
    
    return this.client.post('/kyc-documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  async uploadKycVideo(video: File, documentType: 'id_card' | 'passport' | 'proof_of_address'): Promise<AxiosResponse<ApiResponse<any>>> {
    const formData = new FormData()
    formData.append('video', video)
    formData.append('document_type', documentType)
    
    return this.client.post('/kyc/upload-video/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  async getKycDocuments(params?: {
    document_type?: 'id_card' | 'passport' | 'proof_of_address'
    verification_status?: 'pending' | 'approved' | 'rejected'
  }): Promise<AxiosResponse<ApiResponse<any[]>>> {
    return this.client.get('/kyc-documents/', { params })
  }

  // Contact endpoints
  async getContacts(): Promise<AxiosResponse<ApiResponse<Contact[]>>> {
    return this.client.get('/contacts')
  }

  async getContact(id: string): Promise<AxiosResponse<ApiResponse<Contact>>> {
    return this.client.get(`/contacts/${id}`)
  }

  async createContact(data: {
    name: string
    phone_number?: string
    email?: string
    country_code?: string
  }): Promise<AxiosResponse<ApiResponse<Contact>>> {
    return this.client.post('/contacts', data)
  }

  async updateContact(id: string, data: Partial<Contact>): Promise<AxiosResponse<ApiResponse<Contact>>> {
    return this.client.put(`/contacts/${id}`, data)
  }

  async deleteContact(id: string): Promise<AxiosResponse<ApiResponse<null>>> {
    return this.client.delete(`/contacts/${id}`)
  }

  async searchContacts(searchTerm: string): Promise<AxiosResponse<ApiResponse<Contact[]>>> {
    return this.client.get('/contacts/search', { params: { search: searchTerm } })
  }

  async discoverDjeloUsers(contacts: Array<{ name: string; phone?: string; email?: string }>): Promise<AxiosResponse<ApiResponse<DiscoveredDjeloUser[]>>> {
    return this.client.post('/contacts/discover_djelo_users/', { contacts })
  }

  async getIncomingFriendRequests(): Promise<AxiosResponse<ApiResponse<FriendRequest[]>>> {
    return this.client.get('/friend-requests/incoming/')
  }

  async createFriendRequest(recipientUserId: string): Promise<AxiosResponse<ApiResponse<FriendRequest>>> {
    return this.client.post('/friend-requests/', { recipient_user_id: recipientUserId })
  }

  async acceptFriendRequest(id: string): Promise<AxiosResponse<ApiResponse<FriendRequest>>> {
    return this.client.post(`/friend-requests/${id}/accept/`)
  }

  async rejectFriendRequest(id: string): Promise<AxiosResponse<ApiResponse<FriendRequest>>> {
    return this.client.post(`/friend-requests/${id}/reject/`)
  }

  async createMoneyRequest(data: {
    recipient_user_id?: string
    recipient_phone?: string
    recipient_email?: string
    amount: number
    description?: string
  }): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.client.post('/money-requests/', data)
  }

  // Reports endpoints
  async getTransactionHistory(params?: {
    start_date?: string
    end_date?: string
    currency?: string
  }): Promise<AxiosResponse<ApiResponse<any[]>>> {
    return this.client.get('/reports/transactions', { params })
  }

  async getSpendingSummary(params?: {
    period?: string
    month?: number
    year?: number
  }): Promise<AxiosResponse<ApiResponse<any>>> {
    return this.client.get('/reports/spending', { params })
  }

  async getSpendingByCategory(params?: {
    period?: string
    month?: number
    year?: number
  }): Promise<AxiosResponse<ApiResponse<any[]>>> {
    return this.client.get('/reports/spending-by-category', { params })
  }

  async exportReport(params?: {
    format?: 'xls' | 'xml' | 'pdf' | 'txt' | 'csv'
    period?: string
    month?: number
    year?: number
  }): Promise<AxiosResponse<Blob>> {
    return this.client.get('/reports/export', {
      params,
      responseType: 'blob'
    })
  }

  // Stripe payment endpoints
  async createPaymentIntent(data: {
    amount: number
    currency: string
    target_currency: string
    target_amount: string
    wallet_id?: string
  }): Promise<AxiosResponse<{ client_secret: string; payment_intent_id: string }>> {
    return this.client.post('/wallets/create_payment_intent/', data)
  }

  async confirmPayment(data: {
    payment_intent_id: string
    amount: number
    currency: string
    std_amount: string
    wallet_id?: string
  }): Promise<AxiosResponse<any>> {
    return this.client.post('/wallets/confirm_payment/', data)
  }

  async createMbReference(data: {
    amount: number
    idempotency_key?: string
  }): Promise<AxiosResponse<{
    reference_id: string
    entity: string
    reference: string
    client_code: string
    amount: string
    status: string
    aeps_file?: string
    aeps_status?: string
    expires_at?: string | null
  }>> {
    return this.client.post('/mb/create_reference/', data)
  }

  // Payment Proof endpoints
  async uploadPaymentProof(file: File, transactionId?: string, notes?: string): Promise<AxiosResponse<ApiResponse<any>>> {
    const formData = new FormData()
    formData.append('file', file)
    if (transactionId) {
      formData.append('transaction_id', transactionId)
    }
    if (notes) {
      formData.append('notes', notes)
    }
    
    return this.client.post('/payment-proofs', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  }

  async getLastDeposit(): Promise<AxiosResponse<any>> {
    return this.client.get('/wallets/get_last_deposit/')
  }

  async createWithdrawRequest(data: {
    std_amount: string
    wallet_id: string
  }): Promise<AxiosResponse<any>> {
    return this.client.post('/wallets/create_withdraw_request/', data)
  }

  async processWithdraw(data: {
    std_amount: string
    wallet_id: string
    payment_intent_id: string
    refund_amount_cents: number
    stripe_currency: string
  }): Promise<AxiosResponse<any>> {
    return this.client.post('/wallets/process_withdraw/', data)
  }

  // Cash withdrawal (pickup at a physical location)
  async previewCashWithdrawal(data: { wallet_id: string; amount: number }): Promise<AxiosResponse<any>> {
    return this.client.post('/withdrawals/preview/', data)
  }

  async createCashWithdrawal(data: { wallet_id: string; amount: number }): Promise<AxiosResponse<any>> {
    return this.client.post('/withdrawals/', data)
  }

  async getCashWithdrawal(id: string): Promise<AxiosResponse<any>> {
    return this.client.get(`/withdrawals/${id}/`)
  }

  async cancelCashWithdrawal(id: string): Promise<AxiosResponse<any>> {
    return this.client.post(`/withdrawals/${id}/cancel/`)
  }

  async getCashWithdrawalLocations(): Promise<AxiosResponse<any>> {
    return this.client.get('/withdrawals/locations/')
  }
}

export const apiClient = new ApiClient()

// Export individual API modules
export const authApi = {
  login: (credentials: LoginRequest) => apiClient.login(credentials),
  register: (data: RegisterRequest) => apiClient.register(data),
  refreshToken: (token: string) => apiClient.refreshToken(token),
  logout: () => apiClient.logout(),
  requestPasswordReset: (email: string) => apiClient.requestPasswordReset(email),
  confirmPasswordReset: (data: { token: string; new_password: string; confirm_password: string }) => apiClient.confirmPasswordReset(data),
}

export const userApi = {
  getProfile: () => apiClient.getProfile(),
  updateProfile: (data: Partial<User>) => apiClient.updateProfile(data),
  getUserProfile: () => apiClient.getUserProfile(),
  updateUserProfile: (data: {
    first_name?: string
    last_name?: string
    address?: string
    city?: string
    postal_code?: string
    date_of_birth?: string
    nationality?: string
  }) => apiClient.updateUserProfile(data),
  searchUsers: (query: string) => apiClient.searchUsers(query),
}

export const walletApi = {
  getWallets: () => apiClient.getWallets(),
  getWallet: (id: string) => apiClient.getWallet(id),
  getWalletFeeSchedule: (id: string) => apiClient.getWalletFeeSchedule(id),
  createWallet: (data: { currency: string; daily_limit?: number; monthly_limit?: number }) => apiClient.createWallet(data),
  updateWalletLimits: (id: string, data: { daily_limit?: number; monthly_limit?: number }) => apiClient.updateWalletLimits(id, data),
  deleteWallet: (id: string) => apiClient.deleteWallet(id),
  setDefaultWallet: (id: string) => apiClient.setDefaultWallet(id),
  transferMoney: (fromWalletId: string, data: { to_wallet_id: string; amount: number; description?: string }) => apiClient.transferMoney(fromWalletId, data),
  generateReceiveQR: (data: { wallet_id: string; amount: number; description?: string }) => apiClient.generateReceiveQR(data),
  lookupReceiveQR: (qrHash: string) => apiClient.lookupReceiveQR(qrHash),
  processReceiveQR: (data: { qr_hash: string; wallet_id: string; amount: number; currency?: string; description?: string }) => apiClient.processReceiveQR(data),
  previewCashWithdrawal: (data: { wallet_id: string; amount: number }) => apiClient.previewCashWithdrawal(data),
  createCashWithdrawal: (data: { wallet_id: string; amount: number }) => apiClient.createCashWithdrawal(data),
  getCashWithdrawal: (id: string) => apiClient.getCashWithdrawal(id),
  cancelCashWithdrawal: (id: string) => apiClient.cancelCashWithdrawal(id),
  getCashWithdrawalLocations: () => apiClient.getCashWithdrawalLocations(),
}

export const transactionApi = {
  getTransactions: (params?: any) => apiClient.getTransactions(params),
  getTransaction: (id: string) => apiClient.getTransaction(id),
  createTransaction: (data: any) => apiClient.createTransaction(data),
  sendMoney: async (data: { 
    to_wallet_id?: string
    to_email?: string
    to_phone?: string
    from_wallet_id?: string
    amount: number
    description?: string
  }) => {
    // Get wallet to determine currency
    let currency = 'STN'
    if (data.from_wallet_id) {
      try {
        const walletResponse = await apiClient.getWallet(data.from_wallet_id)
        const wallet = walletResponse.data?.data || walletResponse.data
        if (wallet?.currency_code || wallet?.currency) {
          currency = wallet.currency_code || wallet.currency
        }
      } catch (error) {
        // If wallet fetch fails, use default currency
        console.warn('Could not fetch wallet currency, using STN')
      }
    }
    
    return apiClient.createTransaction({
      from_wallet_id: data.from_wallet_id,
      to_wallet_id: data.to_wallet_id,
      to_phone: data.to_phone,
      to_email: data.to_email,
      amount: data.amount,
      currency: currency,
      description: data.description,
    })
  },
}

export const qrApi = {
  scanQRCode: (qrData: string) => apiClient.scanQRCode(qrData),
  processQRPayment: (data: any) => apiClient.processQRPayment(data),
}

export const merchantApi = {
  getMerchants: (params?: any) => apiClient.getMerchants(params),
  getMerchant: (id: string) => apiClient.getMerchant(id),
  getCategories: () => apiClient.getMerchantCategories(),
  submitBusinessApplication: (formData: FormData) => apiClient.submitBusinessApplication(formData),
  getBusinessApplications: () => apiClient.getBusinessApplications(),
  createReview: (data: { merchant_location: string, rating: number, comment?: string }) => apiClient['client'].post('/map/reviews', data),
  getFavorites: (locationId?: string) => apiClient['client'].get('/map/favorites', { params: locationId ? { merchant_location: locationId } : undefined }),
  addFavorite: (locationId: string) => apiClient['client'].post('/map/favorites', { merchant_location: locationId }),
  removeFavoriteById: (favoriteId: string) => apiClient['client'].delete(`/map/favorites/${favoriteId}`),
  getLocationReviews: (locationId: string) => apiClient['client'].get(`/map/locations/${locationId}/reviews`),
}

export const notificationApi = {
  getNotifications: () => apiClient.getNotifications(),
  markAsRead: (id: string) => apiClient.markNotificationAsRead(id),
  markAllAsRead: () => apiClient.markAllNotificationsAsRead(),
  getUnreadCount: () => apiClient.getUnreadCount(),
}

export const contactApi = {
  getContacts: () => apiClient.getContacts(),
  getContact: (id: string) => apiClient.getContact(id),
  createContact: (data: { name: string; phone_number?: string; email?: string; country_code?: string }) => apiClient.createContact(data),
  updateContact: (id: string, data: Partial<Contact>) => apiClient.updateContact(id, data),
  deleteContact: (id: string) => apiClient.deleteContact(id),
  searchContacts: (searchTerm: string) => apiClient.searchContacts(searchTerm),
  discoverDjeloUsers: (contacts: Array<{ name: string; phone?: string; email?: string }>) => apiClient.discoverDjeloUsers(contacts),
  getIncomingFriendRequests: () => apiClient.getIncomingFriendRequests(),
  createFriendRequest: (recipientUserId: string) => apiClient.createFriendRequest(recipientUserId),
  acceptFriendRequest: (id: string) => apiClient.acceptFriendRequest(id),
  rejectFriendRequest: (id: string) => apiClient.rejectFriendRequest(id),
  createMoneyRequest: (data: { recipient_user_id?: string; recipient_phone?: string; recipient_email?: string; amount: number; description?: string }) => apiClient.createMoneyRequest(data),
}

export const kycApi = {
  uploadDocument: (file: File, documentType: 'id_card' | 'passport' | 'proof_of_address') => apiClient.uploadKycDocument(file, documentType),
  uploadVideo: (video: File, documentType: 'id_card' | 'passport' | 'proof_of_address') => apiClient.uploadKycVideo(video, documentType),
  getDocuments: (params?: { document_type?: 'id_card' | 'passport' | 'proof_of_address'; verification_status?: 'pending' | 'approved' | 'rejected' }) => apiClient.getKycDocuments(params),
}

export const reportsApi = {
  getTransactionHistory: (params?: { start_date?: string; end_date?: string; currency?: string }) => apiClient.getTransactionHistory(params),
  getSpendingSummary: (params?: { period?: string; month?: number; year?: number }) => apiClient.getSpendingSummary(params),
  getSpendingByCategory: (params?: { period?: string; month?: number; year?: number }) => apiClient.getSpendingByCategory(params),
  exportReport: async (params?: { format?: 'xls' | 'xml' | 'pdf' | 'txt' | 'csv'; period?: string; month?: number; year?: number }) => {
    const response = await apiClient.exportReport(params)
    // Trigger download
    const format = params?.format || 'txt'
    const period = params?.period || 'month'
    const blob = response.data
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `report-${period}-${new Date().toISOString().split('T')[0]}.${format}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    return response
  },
}
